<script setup>
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useSupportTicketFormatters } from '../composables/useSupportTicketFormatters';

const props = defineProps({
  selectedRealEstate: { type: Object, default: () => ({}) },
  selectedResponsible: { type: Object, default: () => ({}) },
  selectedTeamHandover: { type: Object, default: () => ({}) },
  selectedOperationalTimeline: { type: Array, default: () => [] },
  selectedSla: { type: Object, default: null },
  assistanceContextOptions: { type: Array, default: () => [] },
  priorityOptions: { type: Array, default: () => [] },
  teamOptions: { type: Array, default: () => [] },
  canViewAccountTickets: { type: Boolean, default: false },
  canUpdateTeamHandover: { type: Boolean, default: false },
  isUpdatingTeamHandover: { type: Boolean, default: false },
  imovelLink: { type: [String, Object], default: '' },
});

const emit = defineEmits(['submit']);

const teamHandoverForm = defineModel({ type: Object, required: true });

const { t } = useI18n();
const {
  localizedDate,
  emptyValue,
  assistanceLabel,
  priorityLabel: optionPriorityLabel,
} = useSupportTicketFormatters();

const priorityLabel = priority =>
  optionPriorityLabel(props.priorityOptions, priority);
</script>

<template>
  <section
    class="grid gap-4 rounded-lais border border-lais-border-soft bg-white px-4 py-3"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 class="text-[13px] font-semibold text-lais-text">
          {{ t('SUPPORT_TICKETS.OPERATION.TITLE') }}
        </h3>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          {{ t('SUPPORT_TICKETS.OPERATION.HELPER') }}
        </p>
      </div>
      <span
        class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold text-lais-primary"
      >
        {{
          assistanceLabel(assistanceContextOptions, selectedRealEstate.context)
        }}
      </span>
    </div>

    <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <div
        class="rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-2"
      >
        <p class="text-[11px] font-semibold text-lais-text-muted">
          {{ t('SUPPORT_TICKETS.OPERATION.PROPERTY') }}
        </p>
        <p class="mt-1 text-[13px] font-semibold text-lais-text">
          <RouterLink v-if="imovelLink" :to="imovelLink" class="text-lais-link">
            {{ selectedRealEstate.imovel.label }}
          </RouterLink>
          <span v-else>
            {{ emptyValue(selectedRealEstate.imovel?.label) }}
          </span>
        </p>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          {{ emptyValue(selectedRealEstate.imovel?.address) }}
        </p>
      </div>

      <div
        class="rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-2"
      >
        <p class="text-[11px] font-semibold text-lais-text-muted">
          {{ t('SUPPORT_TICKETS.OPERATION.LEASE_CONTRACT') }}
        </p>
        <p class="mt-1 text-[13px] font-semibold text-lais-text">
          {{
            emptyValue(
              selectedRealEstate.lease?.reference ||
                selectedRealEstate.contract?.lease_reference
            )
          }}
        </p>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          {{
            emptyValue(
              selectedRealEstate.lease?.status ||
                selectedRealEstate.contract?.lease_status
            )
          }}
        </p>
        <p
          v-if="selectedRealEstate.lease?.starts_at"
          class="mt-1 text-[12px] text-lais-text-muted"
        >
          {{
            t('SUPPORT_TICKETS.OPERATION.LEASE_PERIOD', {
              start: localizedDate(selectedRealEstate.lease.starts_at),
              end: localizedDate(selectedRealEstate.lease.ends_at),
            })
          }}
        </p>
      </div>

      <div
        class="rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-2"
      >
        <p class="text-[11px] font-semibold text-lais-text-muted">
          {{ t('SUPPORT_TICKETS.OPERATION.HANDOVER') }}
        </p>
        <p class="mt-1 text-[13px] font-semibold text-lais-text">
          {{ emptyValue(selectedRealEstate.handover?.status) }}
        </p>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          {{
            t('SUPPORT_TICKETS.OPERATION.HANDOVER_COUNTS', {
              pending: emptyValue(
                selectedRealEstate.handover?.pending_item_count
              ),
              evidence: emptyValue(selectedRealEstate.handover?.evidence_count),
            })
          }}
        </p>
        <p
          v-if="selectedRealEstate.handover?.accepted_at"
          class="mt-1 text-[12px] text-lais-text-muted"
        >
          {{
            t('SUPPORT_TICKETS.OPERATION.ACCEPTED_AT', {
              date: localizedDate(selectedRealEstate.handover.accepted_at),
            })
          }}
        </p>
      </div>

      <div
        class="rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-2"
      >
        <p class="text-[11px] font-semibold text-lais-text-muted">
          {{ t('SUPPORT_TICKETS.OPERATION.RESPONSIBLE_SLA') }}
        </p>
        <p class="mt-1 text-[13px] font-semibold text-lais-text">
          {{
            emptyValue(
              selectedResponsible.assignee?.name ||
                selectedResponsible.team?.name
            )
          }}
        </p>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          {{
            t('SUPPORT_TICKETS.OPERATION.CATEGORY_PRIORITY', {
              category: emptyValue(selectedRealEstate.category),
              priority: priorityLabel(selectedRealEstate.priority),
            })
          }}
        </p>
        <p class="mt-1 text-[12px] text-lais-text-muted">
          {{
            t('SUPPORT_TICKETS.OPERATION.RESOLUTION_DUE', {
              date: localizedDate(selectedSla?.resolution_due_at),
            })
          }}
        </p>
      </div>
    </div>

    <form
      v-if="canViewAccountTickets"
      class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3 lg:grid-cols-[minmax(180px,260px)_minmax(0,1fr)_auto] lg:items-end"
      @submit.prevent="emit('submit')"
    >
      <LaisField :label="t('SUPPORT_TICKETS.OPERATION.TEAM')">
        <select
          v-model="teamHandoverForm.assigned_team_id"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          :disabled="isUpdatingTeamHandover"
        >
          <option
            v-for="item in teamOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </select>
      </LaisField>
      <LaisField :label="t('SUPPORT_TICKETS.OPERATION.REASON')">
        <input
          v-model.trim="teamHandoverForm.reason"
          type="text"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          :placeholder="t('SUPPORT_TICKETS.OPERATION.REASON_PLACEHOLDER')"
          :disabled="isUpdatingTeamHandover"
        />
      </LaisField>
      <LaisPrimaryButton
        type="submit"
        icon="i-lucide-arrow-right-left"
        :is-loading="isUpdatingTeamHandover"
        :disabled="!canUpdateTeamHandover"
      >
        {{ t('SUPPORT_TICKETS.OPERATION.HANDOVER_SUBMIT') }}
      </LaisPrimaryButton>
    </form>

    <div
      v-if="selectedTeamHandover.latest_transfer"
      class="rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-2 text-[12px] text-lais-text-muted"
    >
      {{
        t('SUPPORT_TICKETS.OPERATION.LATEST_HANDOVER', {
          from: emptyValue(selectedTeamHandover.latest_transfer.from_team_name),
          to: emptyValue(selectedTeamHandover.latest_transfer.to_team_name),
          reason: selectedTeamHandover.latest_transfer.reason,
        })
      }}
    </div>

    <div
      v-if="selectedOperationalTimeline.length"
      class="grid gap-2 rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3"
    >
      <p class="text-[12px] font-semibold text-lais-text">
        {{ t('SUPPORT_TICKETS.OPERATION.HISTORY') }}
      </p>
      <div
        v-for="item in selectedOperationalTimeline"
        :key="`${item.event_type}-${item.id}`"
        class="flex flex-wrap items-center justify-between gap-2 text-[12px] text-lais-text-muted"
      >
        <span class="min-w-0">
          <strong class="font-semibold text-lais-text">
            {{ item.label }}
          </strong>
          <span v-if="item.team_handover">
            {{
              t('SUPPORT_TICKETS.OPERATION.HISTORY_HANDOVER', {
                from: emptyValue(item.team_handover.from_team_name),
                to: emptyValue(item.team_handover.to_team_name),
              })
            }}
          </span>
          <span
            v-if="item.visibility === 'internal'"
            class="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
          >
            {{ t('SUPPORT_TICKETS.DETAIL.INTERNAL') }}
          </span>
        </span>
        <span>{{ localizedDate(item.created_at) }}</span>
      </div>
    </div>
  </section>
</template>
